import { useState } from "react";
import { HomeframeInput } from "@builtbyted/homeframe";
import { api } from "./api";
import { useData, Section, Message, shortDate, timeAgo } from "./ui";

interface Invite {
  id: string;
  name: string;
  expires: number;
  status: "pending" | "accepted" | "expired" | "revoked";
}
interface Viewer {
  id: string;
  name: string;
  revoked: number | null;
  last_used: number | null;
}
interface Access {
  invites: Invite[];
  viewers: Viewer[];
}
interface Created {
  id: string;
  name: string;
  url: string;
  expires: number;
}

export function InviteManager() {
  const data = useData<Access>("/auth/invites", 15000);
  const [name, setName] = useState("");
  const [created, setCreated] = useState<Created | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [confirm, setConfirm] = useState<string | null>(null);
  const revoke = async (kind: "invites" | "viewers", id: string) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await api(`/auth/${kind}/${id}/revoke`, {});
      if (created?.id === id) setCreated(null);
      setConfirm(null);
      setNotice(
        kind === "viewers"
          ? "Viewer access revoked. All their sessions have been signed out."
          : "Invite revoked.",
      );
      data.refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section title="Invites & access">
      <div className="settings-card invite-manager">
        <p>
          Invite someone to see everything with view-only access. Give the
          invite a name, copy the link, and send it. They only need to create a
          passkey.
        </p>
        <form
          className="toolbar"
          onSubmit={async (event) => {
            event.preventDefault();
            setBusy(true);
            setError("");
            setNotice("");
            try {
              const result = await api<Created>("/auth/invites", { name });
              setCreated(result);
              setName("");
              data.refresh();
            } catch (e) {
              setError((e as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          <label className="invite-name">
            Invite name
            <HomeframeInput
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. Alex"
              maxLength={80}
              required
            />
          </label>
          <button className="primary-button" disabled={busy || !name.trim()}>
            Create invite link
          </button>
        </form>
        <p className="subtle">
          Links expire after 7 days and can be used once. Accepted access lasts
          until you revoke it.
        </p>
        {created && (
          <div className="created-invite" role="status">
            <strong>Invite for {created.name}</strong>
            <p>
              Copy this link before leaving this page. Expires{" "}
              {shortDate(created.expires)}.
            </p>
            <div className="toolbar">
              <HomeframeInput
                aria-label="Invite link"
                value={created.url}
                readOnly
                onFocus={(event) => event.target.select()}
              />
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(created.url);
                    setNotice("Invite link copied.");
                  } catch {
                    setNotice("Select the invite link and copy it manually.");
                  }
                }}
              >
                Copy link
              </button>
            </div>
          </div>
        )}
        {(error || data.error) && (
          <Message error>{error || data.error}</Message>
        )}
        {notice && <p role="status">{notice}</p>}
        {!data.data && !data.error && <Message>Loading access…</Message>}
        {data.data && (
          <>
            <h3>Invite links</h3>
            {!data.data.invites.length && (
              <p className="subtle">No invites yet.</p>
            )}
            <div className="access-list">
              {data.data.invites.map((invite) => (
                <div className="access-row" key={invite.id}>
                  <div>
                    <strong>{invite.name}</strong>
                    <small>
                      {invite.status}
                      {invite.status === "pending"
                        ? ` · Expires ${shortDate(invite.expires)}`
                        : ""}
                    </small>
                  </div>
                  {invite.status === "pending" && (
                    <button
                      disabled={busy}
                      onClick={() => void revoke("invites", invite.id)}
                    >
                      Revoke invite
                    </button>
                  )}
                </div>
              ))}
            </div>
            <h3>Viewer accounts</h3>
            {!data.data.viewers.length && (
              <p className="subtle">Accepted invites will appear here.</p>
            )}
            <div className="access-list">
              {data.data.viewers.map((viewer) => (
                <div className="access-row" key={viewer.id}>
                  <div>
                    <strong>{viewer.name}</strong>
                    <small>
                      {viewer.revoked
                        ? "Access revoked"
                        : `View only · Last sign-in ${timeAgo(viewer.last_used)}`}
                    </small>
                  </div>
                  {!viewer.revoked &&
                    (confirm === viewer.id ? (
                      <div className="access-confirm">
                        <span>Sign out {viewer.name} and revoke access?</span>
                        <button
                          disabled={busy}
                          onClick={() => void revoke("viewers", viewer.id)}
                        >
                          Confirm revoke
                        </button>
                        <button
                          disabled={busy}
                          onClick={() => setConfirm(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        disabled={busy}
                        onClick={() => setConfirm(viewer.id)}
                      >
                        Revoke access
                      </button>
                    ))}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Section>
  );
}
